import { useEffect, useState, useMemo, useCallback } from "react";
import { Loader } from "lucide-react";
import GradientButton from "../UIComponents/GradientButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { getConvertHistory } from "../redux/slices/historySlice";
import Pagination from "../UIComponents/Pagination";

const ROWS_PER_PAGE = 5;
const TABLE_COLUMNS = [
  ["sno", "S.No"],
  ["fromCurrency", "From Currency"],
  ["toCurrency", "To Currency"],
  ["fromAmount", "From Amount"],
  ["toAmount", "To Amount"],
  ["rate", "Rate"],
  ["updatedAt", "Date"]
] as const;

interface ConvertOrder {
  updatedAt: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  rate: number;
}

const ConvertHistory = () => {
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    type: "",
  });
  const [sortConfig, setSortConfig] = useState<{
    field: typeof TABLE_COLUMNS[number][0];
    order: "asc" | "desc";
  }>({
    field: "updatedAt",
    order: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useDispatch<AppDispatch>();
  const { convertHistory, isLoading } = useSelector(
    (state: any) => state.history
  );

  const fetchConvertHistory = useCallback(async () => {
    dispatch(getConvertHistory());
  }, [dispatch]);

  useEffect(() => {
    fetchConvertHistory();
  }, [fetchConvertHistory]);

  const handleReset = () => {
    setFilters({ fromDate: "", toDate: "", type: "" });
    setCurrentPage(1);
  };

  const filteredOrders = useMemo(() => {
    if (!Array.isArray(convertHistory?.result)) return [];

    return convertHistory.result.filter((order: ConvertOrder) => {
      if (!order?.updatedAt) return false;

      const orderDate = new Date(order.updatedAt);
      const from = filters.fromDate ? new Date(`${filters.fromDate}T00:00:00Z`) : null;
      const to = filters.toDate ? new Date(`${filters.toDate}T23:59:59Z`) : null;

      const isAfterFromDate = !from || orderDate >= from;
      const isBeforeToDate = !to || orderDate <= to;
      const isMatchingType = !filters.type || order?.fromCurrency?.toLowerCase() === filters.type.toLowerCase();

      return isAfterFromDate && isBeforeToDate && isMatchingType;
    });
  }, [convertHistory, filters]);

  const sortedOrders = useMemo(() => {
    const { field, order } = sortConfig;

    return [...filteredOrders].sort((a, b) => {
      const getValue = (obj: ConvertOrder) => {
        switch (field) {
          case "sno": return 0; // Handled in render
          case "fromCurrency": return obj.fromCurrency;
          case "toCurrency": return obj.toCurrency;
          case "fromAmount": return obj.fromAmount;
          case "toAmount": return obj.toAmount;
          case "rate": return obj.rate;
          case "updatedAt": return new Date(obj.updatedAt).getTime();
          default: return 0;
        }
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return 0;
      }
    });
  }, [filteredOrders, sortConfig]);

  const totalPages = Math.ceil(sortedOrders.length / ROWS_PER_PAGE);
  const paginatedOrders = useMemo(() => {
    return sortedOrders.slice(
      (currentPage - 1) * ROWS_PER_PAGE,
      currentPage * ROWS_PER_PAGE
    );
  }, [sortedOrders, currentPage]);

  const toggleSort = (field: string) => {
    setSortConfig(prev => ({
      field: field as typeof prev.field,
      order: prev.field === field ? (prev.order === "asc" ? "desc" : "asc") : "asc"
    }));
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return "⇅";
    return sortConfig.order === "asc" ? "▲" : "▼";
  };

  return (
    <div className="p-4">

      {/* Table */}
      <div className="overflow-x-auto mt-11">
        <table className="min-w-full table-auto">
          <thead>
            <tr>
              {TABLE_COLUMNS.map(([field, label]) => (
                <th
                  key={field}
                  className="px-4 py-4 text-[13px] text-left text-black dark:text-white cursor-pointer select-none border-b border-gray-200 dark:border-gray-700 whitespace-nowrap"
                  onClick={() => toggleSort(field)}
                >
                  {label} <span>{getSortIcon(field)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-4 text-center" colSpan={TABLE_COLUMNS.length}>
                  <Loader className="animate-spin mx-auto dark:text-white text-black" />
                </td>
              </tr>
            ) : paginatedOrders.length > 0 ? (
              paginatedOrders.map((order, idx) => (
                <tr
                  key={`${order.updatedAt}-${idx}`}
                  className="hover:bg-gray-50 text-[13px] dark:hover:bg-gray-700 text-black dark:text-white border-b border-gray-200 dark:border-gray-700 whitespace-nowrap"
                >
                  <td className="px-4 py-5">
                    {(currentPage - 1) * ROWS_PER_PAGE + idx + 1}
                  </td>
                  <td className="px-4 py-5 uppercase">{order.fromCurrency}</td>
                  <td className="px-4 py-5 uppercase">{order.toCurrency}</td>
                  <td className="px-4 py-5">{order.fromAmount.toFixed(8)}</td>
                  <td className="px-4 py-5">{order.toAmount.toFixed(8)}</td>
                  <td className="px-4 py-5">{order.rate.toFixed(8)}</td>
                  <td className="px-4 py-5">
                    {new Date(order.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="px-4 py-4 text-center text-black dark:text-white"
                  colSpan={TABLE_COLUMNS.length}
                >
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {/* {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 dark:bg-gray-700"
          >
            Previous
          </button>
          <span className="text-black dark:text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 dark:bg-gray-700"
          >
            Next
          </button>
        </div>
      )} */}

      {totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default ConvertHistory;